// storageUtils.js - CORRECTED to use the dynamic JSON filename while keeping the new folder structure.
import { getAuth } from 'firebase/auth';
import { sanitizeFilename, generateSafeGuideFilename, generateSafeFolderName } from './filenameSanitizer';
import { getPointType, initialPoiState, normalizeMapDisplaySettings, normalizeRelatedGuide, normalizeThingsToSee, orderGuidePointsForExport, stripPoiAudioVariantFields } from './utils';
import { prepareItineraryForPublication } from '../../services/automaticGuideResearch';

/**
 * UTILITY FUNCTIONS
 * =================
 * Helper functions used across multiple exports.
 */

// Shared helper function to format names for Firebase Storage paths consistently.
// DEPRECATED: Use sanitizeFilename from filenameSanitizer.js instead
const normalizeNameForPath = (name) => {
  if (!name) return '';
  return sanitizeFilename(name, {
    maxLength: Infinity,
    preserveExtension: false,
    allowSpaces: false
  });
};

// Helper function to ensure the main metadata ID is purely numeric.
const getNumericMetadataId = (existingId) => {
  if (existingId) {
    const match = String(existingId).match(/^(\d+)/);
    if (match && match[1]) {
      return match[1]; // Return only the initial numeric part
    }
  }
  return `${Date.now()}`; // Fallback if no ID exists or it's not in the correct format
};

// Standard ID generator for brand new points.
const generatePointId = () => `${Date.now()}`;

// Helper function to format language code for paths.
const formatLanguageCode = (code) => {
  if (!code) return 'en-us';
  const parts = code.split(/[-_]/);
  if (parts.length === 1) {
    const lang = parts[0].toLowerCase();
    return (lang === 'en') ? 'en-us' : `${lang}-${lang}`;
  } else {
    return `${parts[0].toLowerCase()}-${parts[1].toLowerCase()}`;
  }
};

const ITINERARY_STORAGE_PROXY_URL = 'https://us-central1-guidesofia-access-control.cloudfunctions.net/backendSaveItineraryJsonToFirebase';
const RETRYABLE_SAVE_STATUSES = new Set([429, 502, 503, 504]);
const TRANSLATION_STATE_SUFFIX = '.translation-state.json';

const sleep = (ms) => new Promise(resolve => setTimeout(resolve, ms));

const postJsonWithRetry = async (url, body, options = {}) => {
  const {
    attempts = 3,
    baseDelayMs = 500,
    headers = {}
  } = options;

  let lastError = null;

  for (let attempt = 1; attempt <= attempts; attempt += 1) {
    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...headers
        },
        body: JSON.stringify(body)
      });

      if (response.ok) {
        return response;
      }

      const errorText = await response.text();
      const message = `Firebase save failed: ${response.status} ${response.statusText} ${errorText ? `- ${errorText}` : ''}`.trim();

      if (!RETRYABLE_SAVE_STATUSES.has(response.status) || attempt === attempts) {
        throw new Error(message);
      }

      lastError = new Error(message);
      console.warn(`Save request failed with ${response.status}; retrying attempt ${attempt + 1}/${attempts}`);
    } catch (error) {
      lastError = error;

      if (attempt === attempts) {
        break;
      }

      console.warn(`Save request error on attempt ${attempt}/${attempts}:`, error.message);
    }

    await sleep(baseDelayMs * (2 ** (attempt - 1)));
  }

  throw lastError || new Error('Firebase save failed');
};

const sanitizeGuideMetadataForSave = (metadata = {}) => {
  const sanitized = { ...metadata };
  delete sanitized.translationSourceSignature;
  delete sanitized.translationSourceSignatureUpdatedAt;
  delete sanitized.translation_source_signature;
  return sanitized;
};

const resolveGuideStorageNames = (metadata = {}) => {
  let correctFolderName;
  let correctFileName;

  if (metadata.filename) {
    correctFileName = sanitizeFilename(metadata.filename, { maxLength: Infinity, preserveHyphens: true });
    correctFolderName = correctFileName.replace(/\.json$/, '');
  } else {
    correctFileName = generateSafeGuideFilename(metadata);
    correctFolderName = generateSafeFolderName(metadata);
  }

  return { correctFolderName, correctFileName };
};

export const buildTranslationStateFilename = (metadata = {}) => {
  const { correctFileName } = resolveGuideStorageNames(metadata);
  return correctFileName.replace(/\.json$/, TRANSLATION_STATE_SUFFIX);
};

export const buildTranslationStateUrl = (guideUrl) => {
  if (!guideUrl) return '';
  return String(guideUrl).replace(/\.json(\?.*)?$/i, `${TRANSLATION_STATE_SUFFIX}$1`);
};

/**
 * EXPORTED FUNCTIONS
 * ==================
 */

// Save itinerary JSON to Firebase Storage via the backend cloud function.
export const saveItineraryToStorage = async (itineraryData, metadata) => {
  try {
    const publicationItinerary = prepareItineraryForPublication(itineraryData);
    const cleanedMetadata = sanitizeGuideMetadataForSave(publicationItinerary?.metadata || metadata || {});
    const { correctFolderName, correctFileName } = resolveGuideStorageNames(cleanedMetadata);

    console.log('DEBUG: Using folder name:', correctFolderName);
    console.log('DEBUG: Using file name:', correctFileName);

    const sanitizedItineraryData = {
      ...publicationItinerary,
      metadata: sanitizeGuideMetadataForSave(publicationItinerary?.metadata || cleanedMetadata),
      points: Array.isArray(publicationItinerary?.points)
        ? orderGuidePointsForExport(publicationItinerary.points).map(point => stripPoiAudioVariantFields(point))
        : []
    };

    // Sanitize country name to ensure no spaces in storage paths (use underscores)
    const safeCountry = (cleanedMetadata.country || 'unknown').trim().toLowerCase().replace(/[^\w]/g, '_').replace(/_+/g, '_');
    const auth = getAuth();
    const currentUser = auth.currentUser;
    if (!currentUser) {
      throw new Error('You must be logged in to save to Firebase');
    }

    const token = await currentUser.getIdToken(true);

    // 2. Send the correct names to the backend in the `storageConfig`.
    const response = await postJsonWithRetry(ITINERARY_STORAGE_PROXY_URL, {
      itineraryData: sanitizedItineraryData,
      storageConfig: {
        country: safeCountry,
        itineraryName: correctFolderName, // This is the folder name
        filename: correctFileName,        // This is the dynamic JSON file name
        forceUpdate: true
      }
    }, {
      headers: {
        Authorization: `Bearer ${token}`
      }
    });

    const result = await response.json();
    console.log('Server response:', result);
    return result;

  } catch (error) {
    console.error('Error in saveItineraryToStorage:', error);
    throw error;
  }
};

// Save a pre-computed routes file (_routes.json) to Firebase Storage.
// The routes file is language-independent and lives alongside the guide JSON files.
export const saveRoutesToStorage = async (routesData, metadata) => {
  try {
    const safeCountry = (metadata.country || 'unknown').trim().toLowerCase().replace(/[^\w]/g, '_').replace(/_+/g, '_');
    const auth = getAuth();
    const currentUser = auth.currentUser;
    if (!currentUser) {
      throw new Error('You must be logged in to save to Firebase');
    }

    const token = await currentUser.getIdToken(true);

    // Derive folder name the same way saveItineraryToStorage does
    let folderName;
    if (metadata.filename) {
      folderName = sanitizeFilename(metadata.filename, { maxLength: Infinity, preserveHyphens: true }).replace(/\.json$/, '');
    } else {
      folderName = generateSafeFolderName(metadata);
    }

    const routesFilename = `${folderName}_routes.json`;

    const response = await postJsonWithRetry(ITINERARY_STORAGE_PROXY_URL, {
      itineraryData: routesData,
      storageConfig: {
        country: safeCountry,
        itineraryName: folderName,
        filename: routesFilename,
        forceUpdate: true
      }
    }, {
      headers: {
        Authorization: `Bearer ${token}`
      }
    });

    const result = await response.json();
    return { ...result, filename: routesFilename };
  } catch (error) {
    console.error('Error in saveRoutesToStorage:', error);
    throw error;
  }
};

export const saveTranslationStateToStorage = async (translationState, metadata) => {
  try {
    const cleanedMetadata = sanitizeGuideMetadataForSave(metadata || {});
    const safeCountry = (cleanedMetadata.country || 'unknown').trim().toLowerCase().replace(/[^\w]/g, '_').replace(/_+/g, '_');
    const auth = getAuth();
    const currentUser = auth.currentUser;
    if (!currentUser) {
      throw new Error('You must be logged in to save to Firebase');
    }

    const token = await currentUser.getIdToken(true);
    const { correctFolderName } = resolveGuideStorageNames(cleanedMetadata);
    const translationStateFilename = buildTranslationStateFilename(cleanedMetadata);

    const response = await postJsonWithRetry(ITINERARY_STORAGE_PROXY_URL, {
      itineraryData: translationState,
      storageConfig: {
        country: safeCountry,
        itineraryName: correctFolderName,
        filename: translationStateFilename,
        forceUpdate: true
      }
    }, {
      headers: {
        Authorization: `Bearer ${token}`
      }
    });

    const result = await response.json();
    return { ...result, filename: translationStateFilename };
  } catch (error) {
    console.error('Error in saveTranslationStateToStorage:', error);
    throw error;
  }
};

// Get public URL for an itinerary.
export const getItineraryPublicUrl = (country, itineraryName, filename, language = 'en', city = '') => {
  if (!country || !itineraryName) return '';

  // Use safe filename generation
  const metadata = { country, language, name: itineraryName };
  const safeFileName = filename ? sanitizeFilename(filename, { preserveHyphens: true }) : generateSafeGuideFilename(metadata);
  const safeFolderName = filename
    ? sanitizeFilename(filename, { maxLength: Infinity, preserveHyphens: true }).replace(/\.json$/, '')
    : generateSafeFolderName(metadata);
  const safeCountry = sanitizeFilename(country, { maxLength: Infinity, preserveExtension: false }).toLowerCase();

  return `https://storage.googleapis.com/ai-tourguide-1dc92.firebasestorage.app/special_itineraries/${safeCountry}/${safeFolderName}/${safeFileName}`;
};

// Check if an itinerary already exists in Firebase.
export const checkExistingItinerary = async (country, itineraryName) => {
  try {
    const auth = getAuth();
    const currentUser = auth.currentUser;
    if (!currentUser) {
      throw new Error('User not authenticated');
    }

    const token = await currentUser.getIdToken();
    const response = await fetch(`https://us-central1-guidesofia-access-control.cloudfunctions.net/getGuides?country=${encodeURIComponent(country)}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      }
    });

    if (!response.ok) throw new Error('Failed to check for existing itinerary');

    const result = await response.json();
    if (!result.success || !Array.isArray(result.guides)) {
      throw new Error(result.error || 'Failed to fetch guides');
    }

    const normalizedCountry = String(country || '').trim().toLowerCase();
    const normalizedItineraryName = String(itineraryName || '').trim().toLowerCase();
    const versions = result.guides.filter((guide) => {
      const guideCountry = String(guide.Country || '').trim().toLowerCase();
      const guideName = String(guide.Name || '').trim().toLowerCase();
      return guideCountry === normalizedCountry && guideName === normalizedItineraryName;
    });

    return {
      exists: versions.length > 0,
      versions
    };
  } catch (error) {
    console.error('Error checking existing itinerary:', error);
    return { exists: false, versions: [] };
  }
};

// Save itinerary to a local file.
export const saveItineraryToFile = async (itineraryData, currentFilename) => {
  try {
    const publicationItinerary = prepareItineraryForPublication(itineraryData);
    const blob = new Blob([JSON.stringify(publicationItinerary, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    let filename = currentFilename || publicationItinerary.metadata.filename;
    
    if (!filename) {
      filename = generateSafeGuideFilename(publicationItinerary.metadata || {});
    }
    
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    
    return { success: true, filename };
  } catch (error) {
    console.error('Error saving itinerary to file:', error);
    throw new Error('Failed to save itinerary to file');
  }
};

// Load itinerary from a local file.
export const loadItineraryFromFile = async (file, currentMetadata) => {
  try {
    const text = await file.text();
    const loadedData = JSON.parse(text);
    
    if (loadedData.metadata && loadedData.points) {
      const hasIntroductoryStop = loadedData.metadata.hasOwnProperty('isIntroductoryStop');
      
      if (!hasIntroductoryStop) {
        const userConfirmed = window.confirm(
          "Warning: This file doesn't contain the 'Introduction by Sofia' setting.\n\n" +
          "The checkbox will be unchecked by default. You can enable it in the Basic Information section if needed.\n\n" +
          "Click OK to continue loading the file."
        );
        if (!userConfirmed) throw new Error('File loading cancelled by user');
      }
      
        return {
          ...(loadedData._builder && { _builder: loadedData._builder }),
          metadata: {
            ...currentMetadata,
            ...loadedData.metadata,
            id: getNumericMetadataId(loadedData.metadata.id),
            feedbackCollect: Boolean(loadedData.metadata.feedbackCollect),
            isIntroductoryStop: hasIntroductoryStop ? loadedData.metadata.isIntroductoryStop : false,
            map_display: normalizeMapDisplaySettings(loadedData.metadata.map_display),
            language: loadedData.metadata.language || currentMetadata.language || 'en',
            updatedAt: new Date().toISOString(),
            _hadIntroductoryStopField: hasIntroductoryStop
          },
          points: loadedData.points.map(point => ({
            ...initialPoiState,
            ...point,
            relatedGuide: normalizeRelatedGuide(point.relatedGuide),
            pointType: getPointType(point),
            thingsToSee: normalizeThingsToSee(point.thingsToSee),
            id: point.id || generatePointId(),
            useSSML: point.useSSML !== undefined ? point.useSSML : true,
            keywords: point.keywords || [],
            sphericalPhotoUrl: point.sphericalPhotoUrl || ''
          }))
      };
    } else if (loadedData.description && loadedData.points) {
      console.warn('Loading API response format - isIntroductoryStop will default to false');
      return {
        ...(loadedData._builder && { _builder: loadedData._builder }),
        metadata: {
          ...currentMetadata,
          id: getNumericMetadataId(loadedData.metadata.id),
          description: loadedData.description,
          theme: loadedData.theme,
          categories: loadedData.categories || [],
          tags: loadedData.tags || [],
          duration: loadedData.duration,
          feedbackCollect: false,
          isIntroductoryStop: false,
          map_display: normalizeMapDisplaySettings(currentMetadata?.map_display),
          updatedAt: new Date().toISOString(),
          _hadIntroductoryStopField: false
        },
        points: loadedData.points.map(point => ({
          ...initialPoiState,
          ...point,
          relatedGuide: normalizeRelatedGuide(point.relatedGuide),
          pointType: getPointType(point),
          thingsToSee: normalizeThingsToSee(point.thingsToSee),
          id: point.id || generatePointId(),
          useSSML: point.useSSML !== undefined ? point.useSSML : true,
          keywords: point.keywords || [],
          sphericalPhotoUrl: point.sphericalPhotoUrl || ''
        }))
      };
    }
    
    throw new Error('Invalid itinerary format');
  } catch (error) {
    console.error('Error loading itinerary from file:', error);
    throw new Error('Failed to load itinerary from file: ' + error.message);
  }
};

const storageUtils = {
  saveItineraryToStorage,
  saveRoutesToStorage,
  getItineraryPublicUrl,
  checkExistingItinerary,
  saveItineraryToFile,
  loadItineraryFromFile,
  normalizeNameForPath,
  formatLanguageCode
};

export { normalizeNameForPath, formatLanguageCode };
export default storageUtils;
